<script setup lang="ts">
/**
 * UsernamePrompt.vue — "Reserve your username"
 *
 * Shown once, after the user finishes Tutorial 1. Deliberately NOT at login:
 * that's already a multi-step middleauth flow, and asking before the user has
 * seen chat means asking before they know what a username is for. By the end of
 * the tutorial they've seen the community side of the app, so the ask lands.
 *
 * Dismissing is permanent — if they X out we never ask again (the field stays
 * available in Settings). Nothing breaks without one: chat falls back to the
 * display name, they're just harder to @mention.
 */
import { ref } from 'vue';
import { useProofreadingBackendStore } from '../store';
import pyrIcon from '../../static/badges/pyr/pyr-icon.png';

const backend = useProofreadingBackendStore();

/** Set once the user dismisses, so the prompt never returns. */
const DISMISS_KEY = 'nge_username_prompt_dismissed_v1';

const visible = ref(false);
const value = ref('');
const error = ref('');
const ok = ref('');
const checking = ref(false);
const saving = ref(false);
/** Set once locked in, so we can show a confirmation instead of just vanishing. */
const claimed = ref('');
let timer: ReturnType<typeof setTimeout> | null = null;

/**
 * Wait until the badge celebration has finished.
 *
 * Finishing Tutorial 1 fires the hero badge overlay, and a fixed delay raced
 * it — the prompt landed on top of the badge art. Instead, watch for the
 * celebration to actually clear before asking, with a ceiling so a stuck
 * overlay can't suppress the prompt forever.
 */
async function waitForCelebration(maxMs = 45000) {
  const start = Date.now();
  const stillCelebrating = () =>
    !!backend.pendingBadgeCelebration ||
    !!document.querySelector('.nge-hero-overlay, .nge-cell-overlay, .nge-batch-overlay');
  while (Date.now() - start < maxMs) {
    if (!stillCelebrating()) {
      // Let the exit transition play out rather than cutting straight in.
      await new Promise(r => setTimeout(r, 900));
      if (!stillCelebrating()) return;
    }
    await new Promise(r => setTimeout(r, 500));
  }
}

/**
 * 'claim'  — the one-time prompt after Tutorial 1.
 * 'change' — opened deliberately from Settings, so none of the "only ask once"
 *            guards apply and there's no celebration to wait for.
 */
const mode = ref<'claim' | 'change'>('claim');
/** True when opened deliberately from Settings rather than by the auto-prompt. */
const forcedOpen = ref(false);

/** Open the prompt. `detail.force` (from Settings) bypasses the one-time guards. */
async function maybeOpen(e?: Event) {
  if (visible.value) return;
  if (!backend.userId) return;                               // not signed in yet
  const force = !!(e as CustomEvent)?.detail?.force;

  forcedOpen.value = force;
  if (force) {
    mode.value = backend.username ? 'change' : 'claim';
    claimed.value = '';
    error.value = '';
    ok.value = '';
    visible.value = true;
    // Start from the current handle when changing; otherwise suggest one.
    if (backend.username) value.value = backend.username;
    else { try { value.value = await backend.suggestUsername(); } catch { /* blank */ } }
    return;
  }

  if (backend.username) return;                              // already has one
  if (localStorage.getItem(DISMISS_KEY) === '1') return;     // asked and declined
  mode.value = 'claim';
  await waitForCelebration();
  if (backend.username) return;                              // set in the meantime
  visible.value = true;
  // Pre-fill something we've already confirmed is free, so the common path is
  // a single click.
  try { value.value = await backend.suggestUsername(); } catch { /* leave blank */ }
}

function dismiss() {
  visible.value = false;
  // Only the automatic prompt counts as "asked and declined". Cancelling a
  // dialog you opened yourself from Settings shouldn't suppress the real ask.
  if (!forcedOpen.value) {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
  }
}

function onInput() {
  error.value = '';
  ok.value = '';
  if (timer) clearTimeout(timer);
  const v = value.value.trim();
  if (!v) return;
  const invalid = backend.validateUsername(v);
  if (invalid) { error.value = invalid; return; }
  checking.value = true;
  timer = setTimeout(async () => {
    try {
      const free = await backend.isUsernameAvailable(v);
      if (value.value.trim() !== v) return;     // typed on since
      if (free) ok.value = 'Available';
      else error.value = 'That username is taken.';
    } finally { checking.value = false; }
  }, 400);
}

async function lockIn() {
  const v = value.value.trim();
  saving.value = true;
  error.value = '';
  try {
    await backend.saveUsername(v);
    // Locking in counts as answering, so don't ask again either way.
    localStorage.setItem(DISMISS_KEY, '1');
    // Confirm rather than just disappearing — the modal vanishing gave no
    // signal that anything had been saved.
    claimed.value = v;
    setTimeout(() => { visible.value = false; }, 2600);
  } catch (e: any) {
    error.value = e?.message || 'Could not save that username.';
  } finally {
    saving.value = false;
  }
}

// Tutorial 1 completion fires this.
document.addEventListener('nge:prompt-username', maybeOpen as EventListener);
</script>

<template>
  <Teleport to="body">
    <Transition name="nge-un">
      <div v-if="visible" class="nge-un-backdrop" @click.self="claimed ? null : dismiss()">
        <!-- Success state: confirm the handle rather than just vanishing. -->
        <div v-if="claimed" class="nge-un-modal nge-un-modal--done">
          <img :src="pyrIcon" class="nge-un-icon nge-un-icon--done" alt="" />
          <div class="nge-un-title">{{ mode === 'change' ? 'Username updated' : "You're locked in" }}</div>
          <p class="nge-un-sub">
            Teammates can now tag you as <strong>@{{ claimed }}</strong> in chat.
            You can change it any time in Settings.
          </p>
          <div class="nge-un-done-handle">@{{ claimed }}</div>
        </div>

        <div v-else class="nge-un-modal">
          <button class="nge-un-x" @click="dismiss" :title="mode === 'change' ? 'Cancel' : 'Not now'">×</button>
          <img :src="pyrIcon" class="nge-un-icon" alt="" />
          <div class="nge-un-title">
            {{ mode === 'change' ? 'Change your username' : 'Lock in your username' }}
          </div>
          <p class="nge-un-sub">
            This is how you'll appear in chat, and how teammates tag you:
            <strong>@{{ value || 'yourname' }}</strong>
          </p>

          <div class="nge-un-row">
            <span class="nge-un-at">@</span>
            <input
              v-model="value"
              class="nge-un-input"
              maxlength="20"
              spellcheck="false"
              autocomplete="off"
              placeholder="yourname"
              @input="onInput"
              @keydown.enter="lockIn"
              @keydown.stop
            />
          </div>

          <div class="nge-un-note" :class="{ 'nge-un-note--err': error, 'nge-un-note--ok': ok && !error }">
            <span v-if="checking">Checking…</span>
            <span v-else-if="error">{{ error }}</span>
            <span v-else-if="ok">{{ ok }}</span>
            <span v-else>3-20 characters. Letters, numbers and underscore.</span>
          </div>

          <div class="nge-un-actions">
            <button
              class="nge-un-save"
              :disabled="saving || !!error || !value.trim()"
              @click="lockIn"
            >{{ saving ? 'Saving…' : (mode === 'change' ? 'Save username' : 'Lock it in') }}</button>
            <button class="nge-un-later" @click="dismiss">{{ mode === 'change' ? 'Cancel' : 'Not now' }}</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.nge-un-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse at center, rgba(0, 12, 30, 0.82), rgba(0, 0, 0, 0.92));
  backdrop-filter: blur(6px) saturate(1.2);
}
.nge-un-modal {
  position: relative;
  width: 380px;
  max-width: calc(100vw - 32px);
  padding: 22px 24px 20px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(4, 6, 14, 0.97), rgba(8, 12, 24, 0.96));
  border: 1px solid rgba(74, 158, 255, 0.22);
  box-shadow: 0 0 60px rgba(0, 150, 255, 0.08), 0 16px 48px rgba(0, 0, 0, 0.5);
  font-family: 'Inter', system-ui, sans-serif;
}
.nge-un-x {
  position: absolute;
  top: 10px; right: 12px;
  background: none; border: none;
  color: rgba(150, 175, 215, 0.6);
  font-size: 1.3em; cursor: pointer;
}
.nge-un-x:hover { color: #cfdcef; }
.nge-un-icon {
  display: block;
  width: 54px;
  height: 54px;
  object-fit: contain;
  margin: 0 auto 12px;
  filter: drop-shadow(0 0 14px rgba(74, 158, 255, 0.45));
}
.nge-un-icon--done {
  width: 62px;
  height: 62px;
  animation: nge-un-pop 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes nge-un-pop {
  0%   { transform: scale(0.6); opacity: 0; }
  60%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

.nge-un-modal--done { text-align: center; }
.nge-un-done-handle {
  margin-top: 14px;
  padding: 9px 0;
  border-radius: 8px;
  background: rgba(0, 255, 150, 0.08);
  border: 1px solid rgba(0, 255, 150, 0.3);
  color: #9ff0c8;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 1.05em;
  font-weight: 600;
}

.nge-un-title {
  font-size: 1.15em;
  font-weight: 600;
  color: #e0ecff;
  margin-bottom: 6px;
  text-align: center;
}
.nge-un-sub {
  font-size: 0.85em;
  line-height: 1.5;
  color: #8b9bb4;
  margin: 0 0 14px;
  text-align: center;
}
.nge-un-sub strong { color: #9db8ff; font-weight: 600; }
.nge-un-row {
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  overflow: hidden;
}
.nge-un-row:focus-within { border-color: rgba(74, 158, 255, 0.5); }
.nge-un-at {
  padding: 0 2px 0 12px;
  color: rgba(150, 175, 215, 0.8);
  font-family: 'Consolas', 'Monaco', monospace;
}
.nge-un-input {
  flex: 1;
  background: none; border: none; outline: none;
  color: #e0e0e0;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.95em;
  padding: 10px 12px 10px 2px;
}
.nge-un-note {
  margin-top: 7px;
  min-height: 16px;
  font-size: 0.76em;
  color: #6f7a8d;
}
.nge-un-note--err { color: #ff9b9b; }
.nge-un-note--ok { color: #7fe0a8; }
.nge-un-actions { display: flex; gap: 8px; margin-top: 16px; }
.nge-un-save {
  flex: 1;
  padding: 9px 0;
  border-radius: 8px;
  border: 1px solid rgba(74, 158, 255, 0.5);
  background: rgba(74, 158, 255, 0.18);
  color: #dbe9ff;
  font-weight: 600;
  font-size: 0.9em;
  cursor: pointer;
}
.nge-un-save:hover:not(:disabled) { background: rgba(74, 158, 255, 0.3); }
.nge-un-save:disabled { opacity: 0.45; cursor: default; }
.nge-un-later {
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: none;
  color: #99a3b5;
  font-size: 0.9em;
  cursor: pointer;
}
.nge-un-later:hover { background: rgba(255, 255, 255, 0.06); color: #cfd6e6; }

.nge-un-enter-active, .nge-un-leave-active { transition: opacity 0.22s ease; }
.nge-un-enter-from, .nge-un-leave-to { opacity: 0; }
</style>
